import React, { FC, useContext, useEffect, useRef, useState } from 'react'

import { AdyenContext2 } from '@Domains/Checkout/Providers/AdyenContextProvider'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep, CheckoutFlowSteps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { buildAdyenComponent } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/useAdyen'
import {
  useAdyenCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/useAdyenCheckoutResponse'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { useInitializePaymentMethodMutation } from '@Generated/graphql'
import { MetadataContext } from '@Globals/Metadata'

import styles from './AdyenApplePay.module.scss'

export const AdyenApplePayButton: FC<CheckoutPaymentComponentProps> = ({
  grandTotal,
  option,
  submitPayment,
}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const {
    adyenConfig,
    adyenInstance,
  } = useContext(AdyenContext2)
  const {
    setActiveStep,
    stepValidations,
  } = useContext(CheckoutFlowContext)
  const {
    store,
    storeURL,
  } = useContext(MetadataContext)
  const { handleAdyenResponse } = useAdyenCheckoutResponse()
  const [adyenComponent, setAdyenComponent] = useState()

  const [initializePaymentMethod] = useInitializePaymentMethodMutation({
    context: {
      domainProxy: true,
      userSpecific: true,
    },
  })

  const paymentMethod = adyenConfig?.paymentMethodsResponse?.paymentMethods?.find(x => x.type === 'applepay')
  const isValidRef = useRef(false)
  useEffect(() => {
    // @ts-ignore
    if (adyenComponent && adyenComponent?.props?.amount) {
      // @ts-ignore
      Object.assign(adyenComponent.props.amount, {
        // @ts-ignore
        ...adyenComponent?.props?.amount,
        value: grandTotal.value?.value,
      })
    }
  }, [adyenComponent, grandTotal.value?.value])

  useEffect(() => {
    const allValid
      = stepValidations[CheckoutFlowStep.ADDRESS]
      && stepValidations[CheckoutFlowStep.SHIPPING]
    isValidRef.current = allValid
  }, [stepValidations])

  useEffect(() => {
    if (adyenComponent) {
      return
    }
    const component = buildAdyenComponent(adyenInstance, 'applepay', ref, adyenConfig, {
      countryCode: store.config?.countryCode,
      amount: {
        currency: store.config?.currencyCode,
        value: grandTotal?.value?.value,
      },
      onInit: (data, actions) => {
        actions.enable()
      },
      onClick: (resolve, reject) => {
        setActiveStep(CheckoutFlowSteps.SUCCESS)
        if (!isValidRef.current) {
          reject()
          return
        }
        resolve()
      },
      onValidateMerchant: (resolve, reject) => {
        initializePaymentMethod({
          variables: {
            payload: {
              displayName: paymentMethod?.configuration?.merchantName,
              merchantIdentifier: paymentMethod?.configuration?.merchantId,
              domainName: window.location.hostname,
            },
            paymentMethodID: option.id || '',
          },
        })
          .then((response) => {
            if (response.data?.initializePaymentMethod?.success !== true) {
              reject(new Error('failed to fetch applepay token'))
              return
            }

            resolve(response.data?.initializePaymentMethod?.payload)
          })
          .catch((error) => {
            reject(error)
          })
      },
      onSubmit: (state) => {
        if (state.isValid && !!isValidRef.current) {
          submitPayment(
            state.data,
            response => handleAdyenResponse(adyenInstance, response),
          )
        }
      },
    })
    setAdyenComponent(component)
  }, [
    adyenComponent,
    adyenConfig,
    adyenInstance,
    grandTotal?.value?.value,
    handleAdyenResponse,
    initializePaymentMethod,
    option?.id,
    paymentMethod?.configuration?.merchantId,
    paymentMethod?.configuration?.merchantName,
    setActiveStep,
    store.config?.countryCode,
    store.config?.currencyCode,
    storeURL,
    submitPayment,
  ])

  return (
    <div key={option.id} className={styles.applePayButton} ref={ref} />
  )
}
